import { el, setChildren, setAttr } from 'redom';

import deliveryChannels from '../constants/delivery-channels';

import QuotedMessagePreview from './quoted-message-preview';

import {
  throttle
} from './utilities';

// eslint-disable-next-line func-style
const defaultContent = () => {
  return [{
    type: 'paragraph',
    padding: [
      5,
      5,
      5,
      5
    ],
    margin: [
      0,
      0,
      0,
      0
    ],
    children: [{
      text: ''
    }]
  }];
};

class MessageInput {

  constructor(configuration) {

    this.conversation = null;
    this.senderId = null;
    this.draft = null;
    this.draftPool = null;
    this.emails = null;
    this.editorComponent = null;
    this.typingEndedTimerReference = null;
    this.content = defaultContent();
    this.quotedMessage = null;

    this.editorRenderer = configuration.editorRenderer;
    this.toFieldRenderer = configuration.toFieldRenderer;
    this.messagesViewer = configuration.messagesViewer;
    this.internetPhone = configuration.internetPhone;
    this.serverInterface = configuration.serverInterface;
    this.createDraft = configuration.createDraft;
    this.onMessageContentChange = configuration.onMessageContentChange || function noop() {};
    this.deleteDraft = configuration.deleteDraft;
    this.publish = configuration.publish;
    this.uploadAttachment = configuration.uploadAttachment;
    this.createAttachment = configuration.createAttachment;
    this.deleteAttachment = configuration.deleteAttachment;
    this.onActivity = configuration.onActivity;
    this.disableAILevel = configuration.disableAILevel;
    this.enableBookmarks = configuration.enableBookmarks;
    this.onActivityThrottled = throttle(this.onActivity, 5000);
    this.onError = configuration.onError || function noop() {};
    this.onSenderChange = configuration.onSenderChange || function noop() {};

    this.editorContainer = el('#inbox-message-input-container');
    this.recipientsInputContainer = el('#inbox-message-recipients-input-container', {
      class: 'hide-scrollbar'
    });
    this.recipientsInputWrapper = el('#recipients-input-container-wrapper.hide-scrollbar', [this.recipientsInputContainer]);
    this.quotedMessagePreview = new QuotedMessagePreview({
      onClear: this.clearQuotedMessage.bind(this)
    });
    this.el = el('#inbox-message-input-main', [
      this.quotedMessagePreview,
      this.editorContainer
    ]);

  }

  resetContent() {

    this.content = defaultContent();

  }

  applyQuotedMessage(message) {

    this.quotedMessage = message;

    this.quotedMessagePreview.update(message);

  }

  setQuotedMessage(message) {

    this.applyQuotedMessage(message);

    if (this.editorComponent !== null) {
      this.focus();
    }

  }

  clearQuotedMessage() {

    this.applyQuotedMessage(null);

  }

  async onmount() {

    const registerForOutsideClick = (await import( /* webpackIgnore: true */ 'https://cdn.venturz.co/modules/utilities/dom.js')).registerForOutsideClick;

    registerForOutsideClick(this.el, this.handleOutsideClick.bind(this));

    if (this.editorComponent !== null) {
      this.editorComponent.remount(this.buildEditorProperties());
      this.displayNewDraft();
    } else {
      // #displayNewDraft calls #emitInputReady. So calling this in the other branch
      this.emitInputReady();
    }

  }

  async onunmount() {

    if (this.editorComponent !== null) {
      this.editorComponent.unmount();
      this.editorComponent = null;
    }

    this.content = [{
      type: 'paragraph',
      padding: [
        5,
        5,
        5,
        5
      ],
      margin: [
        0,
        0,
        0,
        0
      ],
      children: [{
        text: ''
      }]
    }];

  }

  handleOutsideClick() {

    this.hideRecipientsInput();

  }

  buildEditorProperties() {

    const internetPhone = this.internetPhone;

    let attachments = null;
    let content = this.content;
    let draftId = null;

    if (this.draft !== null) {
      draftId = this.draft._id;
      if (this.content.length > 1) {
        this.draft.content = this.content;
        content = this.draft.content;
      } else if (this.content[0].type === 'paragraph' && this.content[0].children[0].text.trim() !== '') {
        this.draft.content = this.content;
        content = this.draft.content;
      } else if (this.content[0].type !== 'paragraph') {
        this.draft.content = this.content;
        content = this.draft.content;
      } else {
        content = this.draft.content;
      }
      attachments = this.draft.attachments;
    }

    const messageInput = this;

    const properties = {
      conversation: this.conversation,
      attachments,
      content,
      onChange: this.handleMessageChange.bind(this),
      onComplete: this.handleMessageCompletion.bind(this),
      mediaUploader: this.uploadConversationMessageAttachment.bind(this),
      createAttachment(attachment) {
        const conversationId = messageInput.draft.conversationId;
        const conversationMessageId = messageInput.draft._id;
        return messageInput.createAttachment(conversationId, conversationMessageId, attachment);
      },
      deleteAttachment(attachmentId) {
        const conversationId = messageInput.draft.conversationId;
        const conversationMessageId = messageInput.draft._id;
        return messageInput.deleteAttachment(conversationId, conversationMessageId, attachmentId);
      },
      readOnly: false,
      onInputReady() {},
      onFocus: this.handleEditorFocus.bind(this),
      onKeyDown: this.handleKeyDown.bind(this),
      disableAILevel: this.disableAILevel,
      internetPhone: this.internetPhone,
      key: Date.now(),
      enableBookmarks: this.enableBookmarks,
      draftId,
      onSenderChange: this.onSenderChange
    };

    if (properties.enableBookmarks === true) {
      properties.bookmarks = {
        bookmarks: [...internetPhone.messagesViewer.bookmarksConfiguration.bookmarks],
        onSelect(bookmark) {
          internetPhone.setContent(bookmark.content);
        },
        onDelete(bookmark) {
          return internetPhone.messagesViewer.unbookmark(bookmark.messageId);
        }
      };
    }

    return properties;

  }

  mountEditor() {

    if (this.editorComponent !== null) {
      return;
    }

    this.editorComponent = this.editorRenderer(this.editorContainer, this.buildEditorProperties()) || null;

  }

  handleEditorFocus() {

    // this.showRecipientsInput();

  }

  handleEditorBlur() {

    this.hideRecipientsInput();

  }

  async tryDeleteExistingDraft() {

    const draft = this.draft;

    if (draft === null) {
      return;
    }

    const conversationId = draft.conversationId;
    const conversationMessageId = draft._id;

    await this.deleteDraft(conversationId, conversationMessageId);

  }

  hideRecipientControls() {

    setChildren(this.el, [this.quotedMessagePreview, this.editorContainer]);

  }

  showRecipientControls() {

    setChildren(this.el, [this.recipientsInputWrapper, this.quotedMessagePreview, this.editorContainer]);

  }

  setContent(content) {

    this.content = content;

    if (this.editorComponent !== null) {
      this.editorComponent.update(this.buildEditorProperties());
    } else {
      this.mountEditor();
    }

  }

  async update(conversation) {

    this.conversation = conversation;
    this.draft = null;
    this.resetContent();
    this.applyQuotedMessage(null);
    this.senderId = this.conversation.lastOutboundMessageSenderId || this.conversation.initialReceiver._id;
    this.hideRecipientControls();
    this.updateRecipientsInput();

    this.handleDraftPoolReady();

  }

  async displayNewDraft() {

    this.draft = await this.createDraft(this.conversation._id, this.senderId);

    if (this.content.length > 1 || this.content[0].children[0].text.trim() !== '') {
      this.draft.content = this.content;
      await this.onMessageContentChange(this.conversation._id, this.draft._id, this.content);
    }

    this.onActivityThrottled = throttle(this.onActivity, 5000);

    this.tryUpdateEditor();
    this.syncSuggestionsVisibility();
    this.emitInputReady();

  }

  async handleDraftPoolReady() {

    this.mountEditor();
    await this.displayNewDraft();

  }

  async tryResendMessage(response, conversationId, conversationMessageId, content, receivers, lastMessage, quotedMessageId) {

    if (response !== null) {
      return response;
    }

    let sent = false;
    let attempts = 0;
    let resendResponse = null;

    while (sent === false && attempts < 3) {
      this.draft = await this.createDraft(this.conversation._id, this.senderId);
      resendResponse = await this.publish(conversationId, this.draft._id, content, receivers, lastMessage, quotedMessageId);
      sent = resendResponse !== null;
      attempts++;
    }
    this.messagesViewer.removeMessageById(conversationMessageId);
    return resendResponse;

  }

  async publishMessage(message, content, serializedContent = null, quotedMessageId = null) {

    const conversationId = message.conversationId;
    const conversationMessageId = message._id;
    const receivers = [this.conversation.helpSeeker];
    const lastMessage = this.messagesViewer.getLatestMessage();

    this.internetPhone.resetSuggestions();
    if (serializedContent !== null) {
      this.messagesViewer.appendMessage({
        ...message,
        content,
        serializedContent,
        quotedMessageId,
        publishedAt: Date.now()
      });
    }

    try {

      let response = await this.publish(conversationId, conversationMessageId, content, receivers, lastMessage, quotedMessageId);

      response = await this.tryResendMessage(response, conversationId, conversationMessageId, content, receivers, lastMessage, quotedMessageId);

      this.hideRecipientsInput();

      this.messagesViewer.handleOutboundConversationMessageUpdate({
        conversation: this.conversation,
        message: response
      });

    } catch (exception) {
      this.messagesViewer.removeMessageById(conversationMessageId);
      this.onError(exception);

    }

  }

  isInputEmpty() {

    return this.isMessageContentEmpty(this.content);

  }

  syncSuggestionsVisibility() {

    if (this.isMessageContentEmpty(this.content) === false) {
      this.internetPhone.hideSuggestions();
    } else {
      this.internetPhone.showSuggestions();
    }

  }

  isMessageContentEmpty(content) {

    if (content.length > 1) {
      return false;
    }

    const node = content[0];

    if (node.type !== 'paragraph') {
      return false;
    }

    if (node.children.length > 1) {
      return false;
    }

    return node.children[0].text.trim() === '';

  }

  updateRecipientsInputVisibility(content) {

    if (this.isMessageContentEmpty(content) === true) {
      this.hideRecipientsInput();
    } else {
      this.showRecipientsInput();
    }

  }

  handleKeyDown(event, empty) {

    if (empty === true) {
      return;
    }
    if (this.draft !== null) {
      const conversationId = this.conversation._id;
      const messageId = this.draft._id;
      const sender = this.draft.sender;
      this.onActivityThrottled(conversationId, {
        type: 'TYPING',
        status: 'STARTED',
        metadata: {
          messageId,
          sender
        }
      });
      clearTimeout(this.typingEndedTimerReference);
      this.typingEndedTimerReference = setTimeout(() => {
        this.onActivity(conversationId, {
          type: 'TYPING',
          status: 'COMPLETED',
          metadata: {
            messageId,
            sender
          }
        });
      }, 4000);
    }

  }

  async handleMessageChange(content) {

    this.content = content;

    this.messagesViewer.containerHeightChanged();

    this.updateRecipientsInputVisibility(content);

    this.syncSuggestionsVisibility();

    if (this.draft !== null) {
      await this.onMessageContentChange(this.conversation._id, this.draft._id, content);
    }

  }

  tryUpdateEditor() {

    // if this is used from assistant, editor is a simple text box.
    // Send button converts the text to editor content & sends the message.
    // So, no action needed.
    if (this.editorComponent === null) {
      return;
    }

    // Otherwise, it is used from Inbox.
    // Its properties can be updated
    this.editorComponent.update(this.buildEditorProperties());

  }

  emitInputReady() {

    const event = new CustomEvent('v:chat-input-ready', {
      detail: {
        conversation: this.conversation,
        draft: this.draft
      }
    });
    this.internetPhone.el.dispatchEvent(event);

  }

  async handleMessageCompletion(content, serializedContent) {

    clearTimeout(this.typingEndedTimerReference);
    this.onActivity(this.draft.conversationId, {
      type: 'TYPING',
      status: 'COMPLETED',
      metadata: {
        messageId: this.draft._id,
        sender: this.draft.sender
      }
    });

    try {

      const draft = this.draft;
      const quotedMessageId = this.quotedMessage?._id ?? null;
      this.draft = null;
      this.resetContent();
      this.applyQuotedMessage(null);

      this.tryUpdateEditor();

      await this.publishMessage(draft, content, serializedContent, quotedMessageId);

      // eslint-disable-next-line no-useless-catch
    } catch (exception) {
      /**
       *
       * Due to some network error, draft was not created.
       * Because of that publishMessage was throwing "Not able to access property in null" error.
       * Right solution would be to block the user from writing message & ask them to confirm network connection.
       * When they say "try again" somehow via UI, displayNewDraft can be called.
       * If it does not throw error, then user can be allowed to write the message.
       * Otherwise same UI could be displayed again.
       * Right now, UI is not my "piece of cake". So, skipping all that with this work around.
       * When then time comes, the right action will be taken.
       *
       * */
      throw exception;

    } finally {
      this.displayNewDraft();
    }

  }

  async uploadConversationMessageAttachment(file) {

    const conversationId = this.draft.conversationId;
    const conversationMessageId = this.draft._id;

    return this.uploadAttachment(conversationId, conversationMessageId, file);

  }

  handleRecipientsChange(emails) {

    this.emails = emails;

  }

  showRecipientsInput() {

    setAttr(this.recipientsInputWrapper, {
      style: {
        display: 'block'
      }
    });

  }

  hideRecipientsInput() {

    setAttr(this.recipientsInputWrapper, {
      style: {
        display: 'none'
      }
    });

  }

  async updateRecipientsInput() {

    if (this.conversation.deliveryChannel !== deliveryChannels.EMAIL) {
      return;
    }

    const rendered = await this.toFieldRenderer(this.recipientsInputContainer, this.conversation);

    if (rendered === true) {
      this.showRecipientControls();
    } else {
      this.hideRecipientControls();
    }

  }

  trySetDraft(data) {

    if (this.conversation._id !== data.conversation._id) {
      return;
    }

    if (this.draft?._id !== data.message._id) {
      return;
    }

    this.draft.content = data.message.content;

    this.tryUpdateEditor();

  }

  bookmarksChanged() {

    if (this.editorComponent === null) {
      return;
    }

    this.editorComponent.update(this.buildEditorProperties());

  }

  updateConversation(conversation) {

    this.conversation = conversation;

    if (this.editorComponent === null) {
      return;
    }

    this.editorComponent.update(this.buildEditorProperties());

  }

  refresh() {

    return this.displayNewDraft();

  }

  focus() {

    if (typeof this.editorComponent.focus !== 'function') {
      return;
    }

    this.editorComponent.focus();

  }

  async displayLatestDraft() {

    this.draft = await this.createDraft(this.conversation._id, this.senderId);
    this.content = this.draft.content;

    this.tryUpdateEditor();
    this.syncSuggestionsVisibility();
    this.emitInputReady();

  }

}

export default MessageInput;
